/**
 * Finding a blend in the log — the list's search box, stage chips and sort
 * control share this so matching is identical wherever it runs.
 *
 * Matching follows the recipe list: case- and accent-insensitive prefix
 * matching, AND across query tokens. The haystack is the session name, the
 * beers in it, the additions, and the stage — the things a blender types when
 * they are looking for "the gueuze with the 2024 lambic" or for "packaged".
 * Notes stay out of it. The stage is in there for the reason `batch-search.ts`
 * has the status in its own: typing the word is the same request as tapping
 * the chip, and a search box that ignores it looks broken.
 *
 * The stage filter is `batch-search.ts`'s status filter, one surface over, and
 * deliberately the same three functions with the same contracts: a blender who
 * has learned the brew log's chips has learned these.
 */

import { normalize } from '@beerwright/catalog';
import { BLEND_STAGES, type BlendDoc, type BlendStage } from '@beerwright/commands';
import { naturalCompare } from '@/lib/natural-sort';

/** Split a string into the tokens {@link blendMatchesQuery} compares. */
function tokensOf(value: string): readonly string[] {
  const normalized = normalize(value);
  return normalized.length === 0 ? [] : normalized.split(' ');
}

/** Name, beers, additions and stage — everything a blend can be found by. */
function searchParts(blend: BlendDoc): readonly string[] {
  return [
    blend.name,
    blend.stage,
    ...blend.components.map((component) => component.name),
    ...blend.additions.map((addition) => addition.name),
  ];
}

/**
 * True when every query token prefixes some token in the blend's name, beers,
 * additions or stage. An empty (or whitespace-only) query matches everything —
 * the stage chips still narrow on their own.
 */
export function blendMatchesQuery(blend: BlendDoc, query: string): boolean {
  const queryTokens = tokensOf(query);
  if (queryTokens.length === 0) return true;
  const haystack = searchParts(blend).flatMap(tokensOf);
  return queryTokens.every((token) => haystack.some((part) => part.startsWith(token)));
}

/**
 * True when the blend is in the selected set of stages. An empty set matches
 * everything. OR, not AND: a blend has exactly one stage, so requiring two
 * would match nothing.
 */
export function blendHasStage(blend: BlendDoc, stages: readonly BlendStage[]): boolean {
  if (stages.length === 0) return true;
  return stages.includes(blend.stage);
}

/** Blends matching a free-text query and an optional set of stages. */
export function filterBlends(
  blends: readonly BlendDoc[],
  options: { readonly query?: string; readonly stages?: readonly BlendStage[] } = {},
): readonly BlendDoc[] {
  const query = options.query ?? '';
  const stages = options.stages ?? [];
  return blends.filter((blend) => blendMatchesQuery(blend, query) && blendHasStage(blend, stages));
}

/**
 * Stages that appear in a blend list, in stage order.
 *
 * `archived` among them, with no special case: a filing drawer you cannot open
 * is a bin, and the brew log's chips treat it the same way.
 */
export function collectBlendStages(blends: readonly BlendDoc[]): readonly BlendStage[] {
  const seen = new Set<BlendStage>();
  for (const blend of blends) seen.add(blend.stage);
  return BLEND_STAGES.filter((stage) => seen.has(stage));
}

/** How the blending log can be ordered. Default is last updated — newest edit first. */
export const BLEND_SORTS = ['updated', 'name'] as const;

/** @see BLEND_SORTS */
export type BlendSort = (typeof BLEND_SORTS)[number];

/** Labels for the sort control. */
export const BLEND_SORT_LABELS: Readonly<Record<BlendSort, string>> = {
  updated: 'Last updated',
  name: 'Name',
};

const COMPARE: Readonly<Record<BlendSort, (a: BlendDoc, b: BlendDoc) => number>> = {
  updated: (a, b) => b.updatedAt - a.updatedAt || b.createdAt - a.createdAt,
  name: (a, b) => naturalCompare(a.name, b.name) || b.updatedAt - a.updatedAt,
};

/**
 * Order a blend list.
 *
 * `updated` is the list's default: the session you just edited stays at the
 * top. Name exists so a cellar of a dozen blends can be scanned alphabetically.
 */
export function sortBlends(blends: readonly BlendDoc[], sort: BlendSort): readonly BlendDoc[] {
  return [...blends].sort(COMPARE[sort]);
}
